export const API_URL = "https://southcentraldev.com/api/zayaskitchen";
export const BILLING_API_URL = "https://southcentraldev.com/api/billing";
export const MAIN_API_URL = "https://southcentraldev.com/api/admin";
export const EMAIL_API_URL = "https://southcentraldev.com/api/email";

export const SITE = "zayas-kitchen";
export const NAME = "Zayas Kitchen";
export const AUTH_CHANGE_EVENT = "zayas-auth-changed";

export const getAccessToken = () => localStorage.getItem("access");
export const getRefreshToken = () => localStorage.getItem("refresh");

function emitAuthChange(reason) {
  window.dispatchEvent(
    new CustomEvent(AUTH_CHANGE_EVENT, {
      detail: { reason },
    }),
  );
}

export function setAccessToken(token) {
  localStorage.setItem("access", token);
  localStorage.setItem("site", SITE);
  emitAuthChange("signed_in");
}

export function hasAnyAuthToken() {
  return Boolean(getAccessToken());
}

export function clearAuthStorage(reason = "signed_out") {
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  localStorage.removeItem("is_superuser");
  localStorage.removeItem("site");
  localStorage.removeItem("acting_site");
  localStorage.removeItem("username");
  localStorage.removeItem("email_verified");
  localStorage.removeItem("permissions");
  emitAuthChange(reason);
}

function storeLoginPayload(data) {
  const user = data?.user || data || {};

  if (!data?.access) {
    clearAuthStorage("invalid_login_response");
    throw new Error("Login did not return a valid session. Please try again.");
  }

  localStorage.setItem("access", data.access);

  if (data?.refresh) {
    localStorage.setItem("refresh", data.refresh);
  }

  if (user?.username) {
    localStorage.setItem("username", user.username);
  }

  if (user?.is_superuser !== undefined) {
    localStorage.setItem("is_superuser", String(user.is_superuser));
  }

  if (user?.email_verified !== undefined) {
    localStorage.setItem("email_verified", String(user.email_verified));
  }

  if (user?.acting_site) {
    localStorage.setItem("acting_site", user.acting_site);
  }

  localStorage.setItem("site", SITE);
  emitAuthChange("signed_in");
}

export async function refreshAccessToken() {
  const refresh = getRefreshToken();

  if (!refresh) {
    return null;
  }

  const response = await fetch(`${MAIN_API_URL}/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site": SITE },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    clearAuthStorage("expired");
    return null;
  }

  const data = await response.json();

  if (data?.access) {
    localStorage.setItem("access", data.access);
    emitAuthChange("refreshed");
  }

  if (data?.refresh) {
    localStorage.setItem("refresh", data.refresh);
  }

  return data?.access || getAccessToken();
}

export async function login({ username, password }) {
  clearAuthStorage("login_reset");

  const response = await fetch(`${MAIN_API_URL}/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Site": SITE },
    body: JSON.stringify({ username, password, site: SITE }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.detail || "Login failed");
  }

  storeLoginPayload(data);
  return data;
}

export function isLoggedIn() {
  return hasAnyAuthToken();
}

export async function authFetch(url, options = {}) {
  let access = getAccessToken();
  const refresh = getRefreshToken();

  const makeRequest = (token) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "X-Site": SITE,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  };

  let response = await makeRequest(access);

  if (response.status === 401 && refresh) {
    access = await refreshAccessToken();

    if (access) {
      response = await makeRequest(access);
    }
  }

  if (response.status === 401) {
    clearAuthStorage("expired");
  }

  return response;
}

export async function fetchMe() {
  const response = await authFetch(`${MAIN_API_URL}/me/`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user");
  }

  return response.json();
}

export async function logout(navigate) {
  const refresh = getRefreshToken();

  try {
    await fetch(`${MAIN_API_URL}/logout/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Site": SITE },
      body: JSON.stringify({ refresh }),
    });
  } catch (err) {
    console.warn("Backend logout failed (ignored):", err);
  }

  clearAuthStorage("signed_out");

  if (navigate) {
    navigate("/");
  } else {
    window.location.href = "/";
  }
}